import { forwardRef, useMemo, useState } from "react";
import {
  EditMSFilterFavouriteFieldPath,
  MSFilterRenderedItemProps,
  MSTableFilterItemRef,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/utils";
import {
  SHFormControlLabel,
  SHRadio,
  SHRadioGroup,
  SHStack,
} from "@components/design-systems";
import {
  MSFilterFavouriteSwitchValues,
  MSFilterItemSize,
} from "@models/model-screener/enums/filterValues";
import { useTheme } from "@mui/material";
import { MSFilterItemLabel } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/label";
import { MSFilterResetIcon } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/reset";
import {
  MSFilterDefaultValueConstants,
  MSFilterFavouriteOptions,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/constants";
import { useMount } from "react-use";
import { Controller, useFormContext } from "react-hook-form";
import { ModelScreenerFilterDTO } from "@models/model-screener/entities/modelScreenerFilter";

export const MSFilterFavourite = forwardRef<
  MSTableFilterItemRef,
  MSFilterRenderedItemProps
>(({ item, itemSize, itemPaths, disabled }: MSFilterRenderedItemProps, ref) => {
  const { palette, shadows } = useTheme();
  const isSmallSize = itemSize === MSFilterItemSize.Small;
  const favouriteFieldPaths = itemPaths as EditMSFilterFavouriteFieldPath;

  const { control, getValues, setValue } =
    useFormContext<ModelScreenerFilterDTO>();

  // States
  const [currentFavouriteValue, setCurrentFavouriteValue] = useState<
    string | undefined
  >();

  const shouldShowResetButton = useMemo(
    () =>
      currentFavouriteValue !==
      MSFilterDefaultValueConstants.FavouriteSwitchValue,
    [currentFavouriteValue],
  );

  const handleOnReset = () => {
    setValue(
      favouriteFieldPaths.switchValue,
      MSFilterDefaultValueConstants.FavouriteSwitchValue,
    );
    setCurrentFavouriteValue(
      MSFilterDefaultValueConstants.FavouriteSwitchValue,
    );
  };

  const handleOnChange = (value: string) => {
    setCurrentFavouriteValue(value);
  };

  // Effects
  useMount(() => {
    const formDataValue = getValues(favouriteFieldPaths.switchValue);
    const switchValue = formDataValue
      ? (formDataValue as MSFilterFavouriteSwitchValues)
      : MSFilterDefaultValueConstants.FavouriteSwitchValue;
    setCurrentFavouriteValue(switchValue);
  });

  return (
    <SHStack
      ref={ref}
      padding={isSmallSize ? 1 : 1.5}
      bgcolor={palette.common.white}
      borderRadius={"12px"}
      boxShadow={shadows[1]}
      gap={1.5}
    >
      <SHStack
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        position={"relative"}
      >
        <MSFilterItemLabel size={itemSize} disabled={disabled}>
          {item?.data?.label}
        </MSFilterItemLabel>

        {shouldShowResetButton && (
          <MSFilterResetIcon
            onClick={handleOnReset}
            disabled={disabled}
            position={"absolute"}
            right={0}
          />
        )}
      </SHStack>

      <Controller
        control={control}
        name={favouriteFieldPaths.switchValue}
        render={({ field: { ref, ...other } }) => {
          return (
            <SHRadioGroup
              onChange={(_, value) => {
                handleOnChange(value);
                other.onChange(value);
              }}
            >
              <SHStack
                direction={"row"}
                width={"100%"}
                paddingX={0.5}
                sx={{
                  "& > *": {
                    flex: 1,
                    width: "100%",
                  },
                }}
              >
                {MSFilterFavouriteOptions.map((option, i) => {
                  return (
                    <SHFormControlLabel
                      checked={option.value === currentFavouriteValue}
                      disabled={disabled}
                      value={option.value}
                      key={i}
                      size="small"
                      label={option.label}
                      control={<SHRadio size={"small"} />}
                      sx={{
                        width: "100%",
                      }}
                    />
                  );
                })}
              </SHStack>
            </SHRadioGroup>
          );
        }}
      />
    </SHStack>
  );
});
